import { uniqBy } from 'lodash';
import React, { createContext, useReducer } from 'react';
import { DocumentDiscussionAPI } from 'api/document/document-discussions';
import { Comment, Discussion } from 'api/document/types/document';

type Action =
  | { type: 'set_discussion'; payload: Discussion[] }
  | { type: 'update_a_comment'; payload: { comment: Comment } }
  | { type: 'delete_a_comment'; payload: { comment: Comment } }
  | { type: 'delete_a_thread'; payload: { comment: Comment } }
  | { type: 'add_a_comment'; payload: { comment: Comment } }
  | { type: 'ws_add_a_comment'; payload: { comment: Comment } }
  | { type: 'create_discussion'; payload: { comment: Comment } };

type Dispatch = (action: Action) => void;
type State = { discussions: Discussion[] };
type DiscussionProviderProps = { children: React.ReactNode };

const DiscussionStateContext = createContext<State | undefined>(undefined);
const DiscussionDispatchContext =
  createContext<Dispatch | undefined>(undefined);

// Reducer. Not using constants for actions as TS will take care of it.
function discussionReducer(state: State, action: Action) {
  switch (action.type) {
    case 'set_discussion': {
      return { discussions: action.payload };
    }
    case 'create_discussion': {
      const newComment = action.payload.comment;

      // There maybe a chance that websocket update came first than the api response and
      // discussion maybe already present in the store.
      const discussionPresent = state.discussions.find(
        (discussion) =>
          discussion.commentThread.threadId === newComment.commentThreadId,
      );

      if (discussionPresent) {
        // check with commentGuid for uniqueness of the comment
        const updatedDiscussions = state.discussions.map((discussion) =>
          discussion.commentThread.threadId === newComment.commentThreadId
            ? {
                ...discussion,
                comments: uniqBy(
                  discussion.comments.map((comment) =>
                    comment.commentId === newComment.commentId
                      ? { ...comment, ...newComment }
                      : comment,
                  ),
                  'commentGuid',
                ),
              }
            : discussion,
        );

        return { discussions: updatedDiscussions };
      }

      return {
        discussions: [
          {
            commentThread: {
              threadId: newComment.commentThreadId,
              isResolved: newComment.resolved,
            },
            comments: [newComment],
          },
          ...state.discussions,
        ],
      };
    }
    case 'update_a_comment': {
      const updatedComment = action.payload.comment;
      const updatedDiscussion = state.discussions.map((discussion) =>
        discussion.commentThread.threadId === updatedComment.commentThreadId
          ? {
              ...discussion,
              comments: discussion.comments.map((oldComment) =>
                oldComment.commentId === updatedComment.commentId
                  ? updatedComment
                  : oldComment,
              ),
            }
          : discussion,
      );

      return { discussions: updatedDiscussion };
    }
    case 'delete_a_comment': {
      const deletedComment = action.payload.comment;
      const updatedDiscussions = state.discussions.map((discussion) =>
        discussion.commentThread.threadId === deletedComment.commentThreadId
          ? {
              ...discussion,
              comments: discussion.comments.filter(
                (oldComment) =>
                  oldComment.commentId !== deletedComment.commentId,
              ),
            }
          : discussion,
      );

      return { discussions: updatedDiscussions };
    }

    case 'delete_a_thread': {
      const deletedComment = action.payload.comment;
      const updatedDiscussions = state.discussions.filter(
        (oldDiscussion) =>
          oldDiscussion.commentThread.threadId !==
          deletedComment.commentThreadId,
      );

      return { discussions: updatedDiscussions };
    }
    case 'add_a_comment': {
      const newComment = action.payload.comment;
      const updatedDiscussion = state.discussions.map((discussion) =>
        discussion.commentThread.threadId === newComment.commentThreadId
          ? {
              ...discussion,
              comments: uniqBy(
                discussion.comments.concat(newComment),
                'commentGuid',
              ),
            }
          : discussion,
      );

      return { discussions: updatedDiscussion };
    }
    case 'ws_add_a_comment': {
      const newComment = action.payload.comment;
      const discussionPresent = state.discussions.find(
        (discussion) =>
          discussion.commentThread.threadId === newComment.commentThreadId,
      );

      if (discussionPresent) {
        const commentPresent = discussionPresent.comments.find(
          (comment) => comment.commentId === newComment.commentId,
        );

        // Add comment in a discussion if the comment is not present already.
        if (!commentPresent) {
          const updatedDiscussion = state.discussions.map((discussion) =>
            discussion.commentThread.threadId === newComment.commentThreadId
              ? {
                  ...discussion,
                  comments: discussion.comments.concat(newComment),
                }
              : discussion,
          );

          return { discussions: updatedDiscussion };
        }

        // Update the comment with latest value
        const updatedDiscussion = state.discussions.map((discussion) =>
          discussion.commentThread.threadId === newComment.commentThreadId
            ? {
                ...discussion,
                comments: discussion.comments.map((comment) =>
                  comment.commentId === newComment.commentId
                    ? { ...comment, ...newComment }
                    : comment,
                ),
              }
            : discussion,
        );

        return { discussions: updatedDiscussion };
      }

      return {
        discussions: [
          {
            commentThread: {
              threadId: newComment.commentThreadId,
            },
            comments: [newComment],
          },
          ...state.discussions,
        ],
      };
    }
    default: {
      throw new Error(`Unhandled action: ${action}`);
    }
  }
}

const DiscussionProvider = ({ children }: DiscussionProviderProps) => {
  const [state, dispatch] = useReducer(discussionReducer, { discussions: [] });

  return (
    <DiscussionStateContext.Provider value={state}>
      <DiscussionDispatchContext.Provider value={dispatch}>
        {children}
      </DiscussionDispatchContext.Provider>
    </DiscussionStateContext.Provider>
  );
};

// for state and dispatches
function useDiscussionsState(): State {
  const context = React.useContext(DiscussionStateContext);

  if (context === undefined) {
    throw new Error(
      'useDiscussionsState must be used within a DiscussionProvider',
    );
  }

  return context;
}
function useDiscussionsDispatch(): Dispatch {
  const context = React.useContext(DiscussionDispatchContext);

  if (context === undefined) {
    throw new Error(
      'useDiscussionsDispatch must be used within a DiscussionProvider',
    );
  }

  return context;
}
function useDiscussions(): [State, Dispatch] {
  return [useDiscussionsState(), useDiscussionsDispatch()];
}

// Data functions
const getDiscussionsOnStore = async (dispatch, { docId, projectId }) => {
  const response = await DocumentDiscussionAPI.getDiscussions(projectId, docId);

  dispatch({ type: 'set_discussion', payload: response.discussions });
};

const createADiscussionOnStore: any = async (
  dispatch,
  { docId, projectId, content, isPrivate },
) => {
  const response = await DocumentDiscussionAPI.createDiscussionThread(
    projectId,
    docId,
    content,
    isPrivate,
  );

  dispatch({ type: 'create_discussion', payload: response });

  return response;
};

const addACommentOnStore = async (
  dispatch,
  { docId, projectId, discussionThreadId, content, isPrivate },
) => {
  const response = await DocumentDiscussionAPI.replyDiscussionThread(
    projectId,
    docId,
    discussionThreadId,
    content,
    isPrivate,
  );

  dispatch({ type: 'add_a_comment', payload: { comment: response.comment } });
};

// ws short for websocket. all ws prefixed functions will be called by pusher related helpers.
const wsAddACommentOnStore = async (dispatch, comment) => {
  dispatch({ type: 'ws_add_a_comment', payload: { comment } });
};

const editCommmentOnStore = async (
  dispatch,
  { docId, projectId, comment, content },
) => {
  dispatch({
    type: 'update_a_comment',
    payload: { comment: { ...comment, content } },
  });
  await DocumentDiscussionAPI.editDiscussionComment(
    projectId,
    docId,
    comment.commentThreadId,
    comment.commentId,
    content,
  );
};

const deleteCommmentOnStore = async (
  dispatch,
  { docId, projectId, comment },
) => {
  dispatch({ type: 'delete_a_comment', payload: { comment } });
  DocumentDiscussionAPI.deleteDiscussionComment(
    projectId,
    docId,
    comment.commentThreadId,
    comment.commentId,
  );
};

const wsDeleteACommentOnStore = async (dispatch, comment) => {
  dispatch({ type: 'delete_a_comment', payload: { comment } });
};

const deleteThreadOnStore = async (dispatch, { docId, projectId, comment }) => {
  dispatch({ type: 'delete_a_thread', payload: { comment } });
  DocumentDiscussionAPI.deleteDiscussionThread(
    projectId,
    docId,
    comment.commentThreadId,
  );
};

const resolveThreadOnStore = async (
  dispatch,
  { docId, projectId, comment },
) => {
  dispatch({ type: 'delete_a_thread', payload: { comment } });
  DocumentDiscussionAPI.resolveDiscussionThread(
    projectId,
    docId,
    comment.commentThreadId,
  );
};

export {
  DiscussionProvider,
  useDiscussions,
  useDiscussionsDispatch,
  getDiscussionsOnStore,
  createADiscussionOnStore,
  addACommentOnStore,
  wsAddACommentOnStore,
  editCommmentOnStore,
  deleteCommmentOnStore,
  wsDeleteACommentOnStore,
  deleteThreadOnStore,
  resolveThreadOnStore,
};
